import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

// *********************************************** GET WAREHOUSES LIST **********************************

export const getWarehouses = async ({
  entityId,
  ...params
}: IWarehouseManagement.IGetWarehousesParams): Promise<IWarehouseManagement.IGetWarehousesListResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/warehouses/${entityId}`,
    {
      params,
    }
  );
  return data;
};
export const useGetWarehouses = ({
  entityId,
  isInventoryActive,
  ...params
}: IWarehouseManagement.IGetWarehousesParams) => {
  return useQuery(
    ['getWarehouses', entityId, isInventoryActive, params],
    () => getWarehouses({ ...params, entityId, isInventoryActive: undefined }),
    {
      enabled: !!entityId && !!isInventoryActive,
    }
  );
};

// *********************************************** GET PRODUCTS LIST **********************************

const getWarehouseProducts = async ({
  entityId,
  warehouseId,
  params,
}: IWarehouseManagement.IWarehouseToEntityParams): Promise<{
  data: IWarehouseManagement.IGetWarehouseProductsResponse;
}> => {
  return await axiosClient.get(
    `/inventory/api/v1/warehouses-stock/${entityId}/${warehouseId}`,
    {
      params,
    }
  );
};
export const useGetWarehouseProducts = ({
  entityId,
  warehouseId,
  params,
}: IWarehouseManagement.IWarehouseToEntityParams) => {
  return useQuery({
    queryKey: ['getWarehouseProducts', warehouseId, params],
    queryFn: () => getWarehouseProducts({ entityId, warehouseId, params }),
    enabled: !!warehouseId && !!entityId,
  });
};
//******************************************Get inbound delivery list***************************

export const getInboundDeliveryList = async ({
  entityId,
  warehouseId,
  params,
}: IWarehouseManagement.IGetInboundDeliveryListParams): Promise<{
  data: IWarehouseManagement.IGetInboundDeliveryListResponse;
}> => {
  return await axiosClient.get(
    `/inventory/api/v1/company-deposit-orders/entity/${entityId}/warehouse/${warehouseId}`,
    {
      params,
    }
  );
};

export const useGetInboundDeliveryList = ({
  entityId,
  warehouseId,
  params,
}: IWarehouseManagement.IGetInboundDeliveryListParams) => {
  return useQuery({
    queryKey: ['inboundDeliveryList', params, warehouseId],
    queryFn: () => getInboundDeliveryList({ entityId, warehouseId, params }),
    enabled: !!warehouseId && !!entityId,
  });
};

//***************************************** Get Product details *****************************

export const getProductDetails = async ({
  entityId,
  productId,
  warehouseId,
  params,
}: IWarehouseManagement.IProductDetailsParams): Promise<IWarehouseManagement.IProduct> => {
  return await axiosClient.get(
    `/inventory/api/v1/warehouses-stock/${entityId}/${warehouseId}/transactions/${productId}`,
    {
      params,
    }
  );
};

export const useGetWarehouseProductDetails = ({
  entityId,
  productId,
  warehouseId,
  params,
}: IWarehouseManagement.IProductDetailsParams) => {
  return useQuery({
    queryKey: ['getWarehouseProductDetails', params, productId, warehouseId],
    queryFn: () =>
      getProductDetails({ entityId, productId, warehouseId, params }),
    enabled: !!productId && !!warehouseId && !!entityId,
  });
};

//***************************************** Get product price-balance ******************

const getBalancePrice = async ({
  entityId,
  productId,
  warehouseId,
}: IWarehouseManagement.IProductDetailsParams): Promise<IWarehouseManagement.IProductBalancePrice> => {
  const response = await axiosClient.get(
    `/inventory/api/v1/warehouses-stock/${entityId}/${warehouseId}/balance-price/${productId}`
  );
  return response.data;
};

export const useGetBalancePrice = ({
  entityId,
  productId,
  warehouseId,
}: IWarehouseManagement.IProductDetailsParams) => {
  return useQuery({
    queryKey: ['warehouseProductBalancePrice', productId, warehouseId],
    queryFn: () => getBalancePrice({ entityId, warehouseId, productId }),
    enabled: !!productId && !!warehouseId && !!entityId,
  });
};

// *************** Get Product Basic info ***************

export const getProductBasicInfo = async (
  productId: number | undefined
): Promise<IWarehouseManagement.IProductBasicInfo> => {
  const response = await axiosClient.get(
    `/inventory/api/v2/products/${productId}`
  );
  return response.data;
};

export const useGetProductBasicInfo = (productId: number | undefined) => {
  return useQuery({
    queryKey: ['productBasicInfo', productId],
    queryFn: () => getProductBasicInfo(productId),
    enabled: !!productId,
  });
};

/******************************************************** add new stock ******************************************************** */

// *********************************************** GET PRODUCTS SUPPLIERS LIST **********************************
// TODO: this function is a duplicate in suppliers with different response
export const getProductsSuppliersList = async (): Promise<
  IGeneral.ISuccessResponseDataSchema<IGeneral.NameIdPair>
> => {
  const { data } = await axiosClient.get(
    '/inventory/api/v1/products-suppliers',
    {
      params: {
        paginated: false,
      },
    }
  );
  return data;
};
export const useGetProductsSuppliersList = () => {
  return useQuery(['getProductsSuppliers'], () => getProductsSuppliersList());
};

// *********************************************** ADD NEW PRODUCT SUPPLIER **********************************

export const useAddNewProductSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
    }): Promise<{ data: IGeneral.NameIdPair }> =>
      axiosClient.post('/inventory/api/v1/products-suppliers', data),
    onSuccess: () => queryClient.invalidateQueries(['getProductsSuppliers']),
  });
};

// *********************************************** ADD NEW WAREHOUSE PRODUCT **********************************
const addWarehouseProduct = async ({
  entityId,
  values,
}: {
  entityId: number | undefined;
  values: IWarehouseManagement.ICreateInboundDeliverySubmissionSchema;
}) => {
  const { data } = await axiosClient.post(
    `/inventory/api/v1/company-deposit-orders/${entityId}`,
    values
  );
  return data;
};
export const useAddNewWarehouseProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addWarehouseProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWarehouseProducts']);
      queryClient.invalidateQueries(['inboundDeliveryList']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};
// *********************************************** GET WAREHOUSE PRODUCT LIST **********************************
export const getProductsList = async ({
  countryCode,
  warehouseId,
  entityId,
  categoryId,
}: IWarehouseManagement.IGetProductsListSchema): Promise<IWarehouseManagement.IGetProductsListResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/products/${countryCode}/warehouse/${entityId}/${warehouseId}/category/${categoryId}`,
    { params: { paginated: false } }
  );
  return data;
};
export const useGetProductsList = (
  params: IWarehouseManagement.IGetProductsListSchema
) => {
  return useQuery(['getProductsList', params], () => getProductsList(params), {
    enabled:
      !!params.countryCode &&
      !!params.warehouseId &&
      !!params.entityId &&
      !!params.categoryId,
  });
};

// *********************************************** get inbound delivery general info **********************************
const getInboundDeliveryGeneralInfo = async ({
  entityId,
  inboundDeliveryId,
}: IWarehouseManagement.IInboundDeliveryIdentifier): Promise<IWarehouseManagement.IInboundDeliveryGeneralInfo> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/company-deposit-orders/general-info/entity/${entityId}/deposit-order/${inboundDeliveryId}`
  );
  return data;
};

export const useGetInboundDeliveryGeneralInfo = (
  params: IWarehouseManagement.IInboundDeliveryIdentifier
) => {
  return useQuery({
    queryKey: ['getInboundDeliveryGeneralInfo', params],
    queryFn: () => getInboundDeliveryGeneralInfo(params),
    enabled: !!params?.entityId && !!params?.inboundDeliveryId,
  });
};

// *********************************************** get inbound delivery batches **********************************
const getInboundDeliveryBatches = async ({
  entityId,
  inboundDeliveryId,
}: IWarehouseManagement.IInboundDeliveryIdentifier): Promise<
  IWarehouseManagement.IInboundDeliveryBatch[]
> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/warehouses-batches/entity/${entityId}/deposit-order/${inboundDeliveryId}`
  );
  return data;
};

export const useGetInboundDeliveryBatches = (
  params: IWarehouseManagement.IInboundDeliveryIdentifier
) => {
  return useQuery({
    queryKey: ['getInboundDeliveryBatches', params],
    queryFn: () => getInboundDeliveryBatches(params),
    enabled: !!params?.entityId && !!params?.inboundDeliveryId,
  });
};

// *********************************************** get inbound delivery history log **********************************
const getInboundDeliveryHistory = async ({
  entityId,
  inboundDeliveryId,
  keyword,
}: IWarehouseManagement.IInboundDeliveryDetailsParams): Promise<
  IWarehouseManagement.IInboundDeliveryHistoryItem[]
> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/company-deposit-orders/entity/${entityId}/deposit-order-history/${inboundDeliveryId}`,
    { params: { keyword } }
  );
  return data;
};

export const useGetInboundDeliveryHistory = (
  params: IWarehouseManagement.IInboundDeliveryDetailsParams
) => {
  return useQuery({
    queryKey: ['getInboundDeliveryHistory', params],
    queryFn: () => getInboundDeliveryHistory(params),
    enabled: !!params?.entityId && !!params?.inboundDeliveryId,
  });
};

// *********************************************** get inbound delivery deleted batches **********************************
const getInboundDeliveryDeletedBatches = async ({
  entityId,
  inboundDeliveryId,
  keyword,
}: IWarehouseManagement.IInboundDeliveryDetailsParams): Promise<
  IWarehouseManagement.IDeletedInboundDeliveryBatch[]
> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/warehouses-batches/entity/${entityId}/deposit-order/${inboundDeliveryId}/deleted`,
    {
      params: { keyword },
    }
  );
  return data;
};

export const useGetInboundDeliveryDeletedBatches = (
  params: IWarehouseManagement.IInboundDeliveryDetailsParams
) => {
  return useQuery({
    queryKey: ['getInboundDeliveryDeletedBatches', params],
    queryFn: () => getInboundDeliveryDeletedBatches(params),
    enabled: !!params?.entityId && !!params?.inboundDeliveryId,
  });
};

// *********************************************** update inbound delivery **********************************
const editInboundDelivery = async ({
  entityId,
  inboundDeliveryId,
  values,
}: IWarehouseManagement.IEditInboundDeliveryParams) => {
  const { data } = await axiosClient.put(
    `/inventory/api/v1/company-deposit-orders/entity/${entityId}/deposit-order/${inboundDeliveryId}`,
    values
  );
  return data;
};

export const useEditInboundDelivery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editInboundDelivery,
    onSuccess: () => {
      queryClient.invalidateQueries(['inboundDeliveryList']);
      queryClient.invalidateQueries(['getInboundDeliveryGeneralInfo']);
      queryClient.invalidateQueries(['getInboundDeliveryBatches']);
      queryClient.invalidateQueries(['getInboundDeliveryHistory']);
      queryClient.invalidateQueries(['getInboundDeliveryDeletedBatches']);
    },
  });
};

// *********************************************** delete inbound delivery **********************************

const deleteInboundDelivery = async ({
  entityId,
  inboundDeliveryId,
}: IWarehouseManagement.IInboundDeliveryIdentifier) => {
  const { data } = await axiosClient.delete(
    `/inventory/api/v1/company-deposit-orders/entity/${entityId}/deposit-order/${inboundDeliveryId}`
  );
  return data;
};

export const useDeleteInboundDelivery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInboundDelivery,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWarehouseProducts']);
      queryClient.invalidateQueries(['inboundDeliveryList']);
    },
  });
};

/******************************************************** DISPATCH REQUESTS ******************************************************** */

// *********************************************** GET WAREHOUSE CATEGORY **********************************
export const getWarehouseCategoriesList = async ({
  warehouseId,
  entityId,
}: IWarehouseManagement.IGetWarehousCategoriesSchema): Promise<
  Array<IGeneral.NameIdPair>
> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v3/warehouses/categories/${entityId}?warehouseId=${warehouseId}`
  );
  return data;
};
export const useGetWarehouseCategoriesList = (
  params: IWarehouseManagement.IGetWarehousCategoriesSchema
) => {
  return useQuery(
    ['getWarehouseCategoriesList', params],
    () => getWarehouseCategoriesList(params),
    { enabled: !!params.warehouseId && !!params.entityId }
  );
};

/******************************************************** DISPATCH REQUESTS ******************************************************** */

// *********************************************** GET DISPATCH REQUESTS LIST **********************************

export const getDispatchRequestsList = async ({
  entityId,
  params,
}: IWarehouseManagement.IDispatchToEntityParams): Promise<IWarehouseManagement.IDispatchRequestsListSchema> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/dispatch-request/${entityId}`,
    { params }
  );
  return data;
};

export const useGetDispatchRequestsList = ({
  entityId,
  params,
}: IWarehouseManagement.IDispatchToEntityParams) => {
  return useQuery({
    queryKey: ['getDispatchRequestsList', entityId, params],
    queryFn: () => getDispatchRequestsList({ entityId, params }),
    enabled: !!entityId && !!params?.status,
  });
};

//************************************************ GET DISPATCH REQUEST DETAILS ******************************

const getDispatchRequestDetails = async ({
  entityId,
  dispatchRequestId,
}: IWarehouseManagement.IDispatchToEntityIdentifier): Promise<IWarehouseManagement.IDispatchRequestDetailsSchema> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/dispatch-request/${entityId}/${dispatchRequestId}`
  );
  return data;
};

export const useGetDispatchRequestDetails = ({
  entityId,
  dispatchRequestId,
}: IWarehouseManagement.IDispatchToEntityIdentifier) => {
  return useQuery({
    queryKey: ['getDispatchRequestDetails', dispatchRequestId],
    queryFn: () => getDispatchRequestDetails({ entityId, dispatchRequestId }),
    enabled: !!entityId && !!dispatchRequestId,
  });
};

////***************************************** REJECT DISPATCH REQUEST ****************************

export const rejectDispatchRequest = async ({
  entityId,
  dispatchRequestId,
  rejectionReason,
}: IWarehouseManagement.RejectDispatchRequestSchema) => {
  const { data } = await axiosClient.put(
    `/inventory/api/v1/dispatch-request/${entityId}/reject/${dispatchRequestId}`,
    { rejectionReason }
  );
  return data;
};

export const useRejectDispatchRequest = () => {
  return useMutation({
    mutationFn: rejectDispatchRequest,
  });
};

//******************************************* GENERATE CHECKOUT BATCHES **************************/

export const generateCheckoutBatches = async ({
  entityId,
  dispatchRequestId,
  warehousesDetails,
}: IWarehouseManagement.ICheckoutDispatchRequestSchema): Promise<IWarehouseManagement.ICheckoutBatchesSchema> => {
  const { data } = await axiosClient.post(
    `/inventory/api/v1/dispatch-request/${entityId}/generate-checkout-batches/${dispatchRequestId}`,
    {
      warehousesDetails: warehousesDetails,
    }
  );
  return data;
};

export const useGenerateCheckoutBatches = ({
  entityId,
  dispatchRequestId,
  warehousesDetails,
}: IWarehouseManagement.ICheckoutDispatchRequestSchema) => {
  return useQuery({
    queryKey: [
      'generateCheckoutBatches',
      entityId,
      dispatchRequestId,
      warehousesDetails,
    ],
    queryFn: () =>
      generateCheckoutBatches({
        entityId,
        dispatchRequestId,
        warehousesDetails,
      }),
    enabled: !!entityId && !!dispatchRequestId && !!warehousesDetails,
  });
};

//********************************************CHECKOUT DISPATCH REQUEST***********************************

export const checkoutDispatchRequest = async ({
  entityId,
  dispatchRequestId,
  warehousesDetails,
}: IWarehouseManagement.ICheckoutDispatchRequestSchema): Promise<IWarehouseManagement.ICheckoutBatchesSchema> => {
  const { data } = await axiosClient.put(
    `/inventory/api/v1/dispatch-request/${entityId}/checkout/${dispatchRequestId}`,
    { warehousesDetails: warehousesDetails }
  );
  return data;
};

export const useCheckoutDispatchRequest = () => {
  return useMutation({
    mutationKey: ['checkoutDispatchRequest'],
    mutationFn: checkoutDispatchRequest,
  });
};

// *********************************************** CUSTODY **********************************

// get custody products
const getCustodyProducts = async ({
  entityId,
  ...params
}: IWarehouseManagement.IGetCustodyProductsParams): Promise<IWarehouseManagement.IGetCustodyProductsResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/custody/products/entity/${entityId}`,
    { params }
  );
  return data;
};

export const useGetCustodyProducts = (
  params: IWarehouseManagement.IGetCustodyProductsParams
) => {
  return useQuery({
    queryKey: ['getCustodyProducts', params],
    queryFn: () => getCustodyProducts(params),
    enabled: !!params?.entityId,
  });
};

// get custody supervisors
const getCompanySupervisors = async (
  params: IGeneral.IQueryParams
): Promise<IGeneral.ISuccessResponseDataSchema<IGeneral.NameIdPair>> => {
  const { data } = await axiosClient.get(
    '/inventory/api/v1/company-users/supervisors',
    { params }
  );
  return data;
};

export const useGetCompanySupervisors = (params: IGeneral.IQueryParams) => {
  return useInfiniteQuery({
    queryKey: ['getCompanySupervisors', params],
    queryFn: ({ pageParam = 1 }) =>
      getCompanySupervisors({ page: pageParam, ...params }),
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
  });
};

// get custody products filter options
const getCustodyProductsFilter = async ({
  entityId,
  params,
}: IWarehouseManagement.IEntityParamsSchema): Promise<IWarehouseManagement.IGetCustodyProductFiltersResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v2/custody/products/entity/${entityId}`,
    { params }
  );
  return data;
};

export const useGetCustodyProductsFilter = ({
  entityId,
  params,
}: IWarehouseManagement.IEntityParamsSchema) => {
  return useInfiniteQuery({
    queryKey: ['getCustodyProductsFilter', entityId, params],
    queryFn: ({ pageParam = 1 }) =>
      getCustodyProductsFilter({
        entityId,
        params: { page: pageParam, ...params },
      }),
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: !!entityId,
  });
};
